import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#f7f2e7]/85">
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute -bottom-24 -left-10 font-script text-[24rem] leading-none text-[#16301f]/[0.06]"
      >
        Parker
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-44 pb-10 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
            className="text-xs uppercase tracking-[0.3em] text-[#16301f]/60 mb-7 flex items-center gap-4"
          >
            <span className="h-px w-12 bg-[#16301f]/40 inline-block" />
            Boutique Furnished Residences · Louisiana
          </motion.p>

          <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#16301f]">
            {["Space to Live.", "Room to Stay."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.16, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.95 }}
            className="font-script text-3xl sm:text-4xl text-[#1a3324] mt-6 -rotate-1 inline-block"
          >
            Distinctive residences, thoughtfully hosted.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
            className="text-sm sm:text-base leading-relaxed text-[#16301f]/65 font-light max-w-xl mt-6"
          >
            A private collection of furnished homes in Baker &amp; Ascension Parish — kept for groups,
            teams, professionals and families who want more than a stay: a better experience.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <button
              data-testid="hero-explore-cta"
              onClick={() => scrollToId("#residences")}
              className="group bg-[#16301f] text-[#f7f2e7] text-xs uppercase tracking-[0.22em] px-7 py-4 flex items-center gap-2 hover:bg-[#284b35] transition-colors duration-300"
            >
              Explore the Collection
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-reserve-cta"
              onClick={() => scrollToId("#inquiry")}
              className="group border border-[#16301f]/30 text-[#16301f] text-xs uppercase tracking-[0.22em] px-7 py-4 flex items-center gap-2 hover:border-[#16301f] transition-colors duration-300"
            >
              Enquire Now
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
            className="relative border border-[#16301f]/25 p-2 sm:p-3"
          >
            <motion.div style={{ y: imgY }} className="overflow-hidden">
              <motion.img
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.6, delay: 0.55, ease: EASE }}
                src="/images/p-delavie-living.jpg"
                alt="De La Vie Maison living room with brick fireplace wall and grey sofas"
                className="kenburns w-full h-[400px] sm:h-[520px] object-cover"
              />
            </motion.div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.8, ease: EASE }}
            className="absolute -bottom-7 -left-3 sm:-left-10 bg-[#fbf8f0] border border-[#16301f]/15 px-6 py-4 shadow-xl"
          >
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#16301f]/60">More than a stay</p>
            <p className="font-script text-2xl sm:text-3xl text-[#1a3324]">A better experience.</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.9 }}
        className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-14 mt-6"
      >
        <div className="grid grid-cols-3 divide-x divide-[#16301f]/15 border-y border-[#16301f]/15 py-6 text-center">
          {[
            ["Three", "Distinctive Residences"],
            ["Baker & Ascension", "Two in Baker · One in Ascension Parish"],
            ["4–8+", "Guests Per Stay"],
          ].map(([big, small]) => (
            <div key={small} className="px-2">
              <p className="font-display text-2xl sm:text-3xl text-[#16301f]">{big}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#16301f]/55 mt-1">{small}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
